import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { RAISERS_EDGE_STAGED_ATTRIBUTES } from '@feathr/blackbox';
import { noop, toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

export interface IRaisersEdgeConnectionControls {
  /** Pauses the sync. Toasts on failure and resolves either way. */
  handlePause: () => Promise<void>;
  /** Resumes the sync. Toasts on failure and resolves either way. */
  handleResume: () => Promise<void>;
  /** Disconnects the integration. Toasts and rejects with the original error on failure, so a confirm modal stays open. */
  handleDisconnect: () => Promise<void>;
  /** Whether a pause, resume or disconnect request is running. */
  isChangingState: boolean;
}

interface IStateChange {
  request: (integration: BlackbaudRaisersEdgeIntegration) => Promise<unknown>;
  failed: (error: string) => string;
  succeeded: string;
  /** A disconnect hides the tabs and stops the sync, so it skips the re-stage and the reload. */
  isDisconnect?: boolean;
}

/**
 * Pause, resume and disconnect for the v2 page. The model applies the server's response as saved,
 * which would drop the edits staged for Save & Sync, so they are staged again afterwards. The page
 * blocks edits and Save & Sync while `isChangingState`, so the snapshot taken before the request
 * is still current when it is staged again.
 */
export function useRaisersEdgeConnectionControls(
  integration: BlackbaudRaisersEdgeIntegration | undefined,
): IRaisersEdgeConnectionControls {
  const { t } = useTranslation();
  const [isChangingState, setIsChangingState] = useState(false);
  // State lags a render behind; the ref blocks a second click that lands before the menu disables.
  const isChangingStateRef = useRef(false);
  // The page's save hook discards staged edits when it unmounts; staging them again then would
  // bring abandoned edits back as unsaved on the next visit.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  /** Toasts and rethrows the original error on failure. */
  async function changeState({
    failed,
    isDisconnect = false,
    request,
    succeeded,
  }: IStateChange): Promise<void> {
    if (!integration || isChangingStateRef.current) {
      return;
    }
    isChangingStateRef.current = true;
    setIsChangingState(true);
    try {
      const staged = integration.getStagedPatch();
      try {
        await request(integration);
      } catch (error) {
        toast(failed(errorMessage(error, t)), { type: 'error' });
        throw error;
      }
      if (!isDisconnect && isMountedRef.current) {
        // A staged attribute the response carried was applied as saved, so stage it again. One the
        // response left out (a 204) is still staged, and setting it again would record it as saved.
        RAISERS_EDGE_STAGED_ATTRIBUTES.filter(
          (attribute) => attribute in staged && !integration.isAttributeDirty(attribute),
        ).forEach((attribute) => {
          integration.set({ [attribute]: staged[attribute] });
        });
        // Pausing or resuming changes the webhook subscriptions the mapping rows report on.
        const store = integration.contactMappingStore;
        if (store.isFetched) {
          void store.refresh();
        }
      }
      toast(succeeded, { type: 'success' });
    } finally {
      isChangingStateRef.current = false;
      setIsChangingState(false);
    }
  }

  async function handlePause(): Promise<void> {
    // changeState has already toasted a failure; pausing has no caller to tell.
    await changeState({
      failed: (error) =>
        t('Something went wrong while trying to pause your integration: {{- error}}', { error }),
      request: (model) => model.pauseIntegration(),
      succeeded: t('Your integration has been successfully paused.'),
    }).catch(noop);
  }

  async function handleResume(): Promise<void> {
    await changeState({
      failed: (error) =>
        t('Something went wrong while trying to resume your integration: {{- error}}', { error }),
      request: (model) => model.resumeIntegration(),
      succeeded: t('Your integration has been successfully resumed.'),
    }).catch(noop);
  }

  function handleDisconnect(): Promise<void> {
    return changeState({
      failed: (error) =>
        t('Something went wrong while trying to disconnect your integration: {{- error}}', {
          error,
        }),
      isDisconnect: true,
      request: (model) => model.disconnectIntegration(),
      succeeded: t('Your integration has been successfully disconnected.'),
    });
  }

  return { handleDisconnect, handlePause, handleResume, isChangingState };
}
